<template>
    <div v-on:keyup.esc="$root.$emit('CLOSERESTOREDIALOG')"
        class="dialog draggable-item bg-light light w-100-sm w-100-md w-75-lg w-50-xl h-75" v-cloak>
        <DialogTitle :title="$t('Configure a restore')" :defaultTitle="$t('Configure a restore')"
            :subTitle="$ONEServiceTypeInfo[$ONEServiceType.HyperV].name"
            @closeDialog="$root.$emit('CLOSERESTOREDIALOG')" />

        <div class="dialog-content p-4 overflow">
            <div v-if="!isAutomaticMode && step === STEP_SELECT_SOURCE && chooseSourceType">
                <div v-if="chooseSourceType" class="row h6 text-bold m-4">
                    <div class="cell-12">{{ $t("Choose source type") }}</div>
                </div>

                <div v-if="chooseSourceType" class="form-group">
                    <ul data-role="listview" data-view="icons" data-select-node="true" data-selectable="false">
                        <li v-for="sourceType in $ONEServiceTypeInfo[restore.type].backupDestinationTypes"
                            :key="sourceType" :data-icon="$ONEServiceTypeInfo[sourceType].icon"
                            :data-caption="$ONEServiceTypeInfo[sourceType].name" @click="setSourceType(sourceType)" />
                    </ul>
                </div>

                <SARepoSelect v-if="![-1, $ONEServiceType.FileFolder].includes(restore.source_type)"
                    :type="restore.source_type" :idSA="restore.source_id_service_account" :idRepo="-2"
                    @sendSATestResult="sendSATestResult" />

                <SelectedResourcesLayout v-if="restore.source_type !== -1" :list="sourceData.selectedNodes"
                    :singleItem="true" :canCancel="false"
                    buttonText="Select the folder with the name of the virtual machine you want to restore"
                    :type="restore.source_type"
                    :isDisabled="restore.source_type !== $ONEServiceType.FileFolder && sourceData.client == null"
                    :heightPercentage="30" @clickFunction="toggleFileSystemSidebar()" />
            </div>

            <div v-if="step === STEP_SELECT_COPY && isAutomaticMode">
                <SARepoSelect v-if="isValid(job) && ![-1, $ONEServiceType.FileFolder].includes(restore.source_type)"
                    :type="restore.source_type" :idSA="restore.source_id_service_account" :idRepo="-2"
                    @sendSATestResult="sendSATestResult" />

                <div class="form-group p-2">
                    <div v-if="wait" data-role="activity" data-type="metro" data-style="dark"></div>
                    <div v-else id="hypervlogslist" class="w-100" style="overflow-y: scroll; overflow-x: hidden;">
                        <div class="text-bold mb-3">{{ $t("Select a copy") }}</div>

                        <ul v-if="logs.length > 0" data-role="listview" data-view="content" data-select-node="true">
                            <li v-for="forlog in logs" :key="forlog.id" class="mb-2"
                                data-icon="<span class='mif-stack'>" :data-caption="convertDate(forlog.dt_start_utc)"
                                :data-content="`<span class='text-muted'>${convertDate(forlog.dt_end_utc)} - ${prettyBytes(forlog.backup_size)} - ${forlog.copied_files} ${$t('files')}</span>`"
                                @click="getJobConfiguration(forlog)" />
                        </ul>
                        <p v-else>{{ $t('No Logs available') }}</p>
                    </div>
                </div>
            </div>

            <div v-if="step === STEP_SELECT_COPY && !isAutomaticMode && HyperVBackupDetails.ibvhconfig">

                <div class="data">
                    <h6 class="text-bold">{{ $t("Backup informations") }}</h6>
                    <p>
                        <span class="text-muted">{{ $t('Virtual Machine') }}</span> :
                        <span>
                            {{ HyperVBackupDetails.ibvhconfig.vmName + ' (' + HyperVBackupDetails.ibvhconfig.vmId + ')'
                            }}
                        </span>
                    </p>
                    <p>
                        <span class="text-muted">{{ $t('Path') }}</span> :
                        <span>{{ HyperVBackupDetails.vmFolder.path }}</span>
                    </p>
                    <p>
                        <span class="text-muted">{{ $t('First run') }}</span> :
                        <span>{{ convertDate(HyperVBackupDetails.ibvhconfig.firstExecution) }}</span>
                    </p>
                </div>

                <div v-if="listBackups.length > 0" id="HyperVBackupList" class="border bd-light p-4 w-100 mb-3 mt-3"
                    style="overflow-y: scroll; overflow-x: hidden;">
                    <h6 class="text-bold mb-3">{{ $t("Select a copy") }}</h6>

                    <ul v-if="listBackups.length > 0" data-role="listview" data-view="content" data-select-node="true"
                        :key="'lb' + listBackups.length">
                        <li v-for="jobInfo in listBackups" :key="jobInfo.numOrder" data-icon="<span class='mif-stack'>"
                            :data-caption="convertDate(jobInfo.dt)" :data-content="getJobInfoContent(jobInfo)"
                            @click="putBackupInHyperVRestoreModel(jobInfo)" />
                    </ul>
                </div>

                <div v-else class="border bd-light p-4 w-100 mb-3 mt-3">
                    <p>{{ $t('No Backup available') }}</p>
                </div>

            </div>

            <div class="row" v-if="step === STEP_SELECT_VM">
                <div v-if="waitvm" data-role="activity" data-type="metro" data-style="dark"></div>
                <div v-else class="border bd-light p-4 w-100" style="overflow-y: scroll; overflow-x: hidden;">
                    <h6 class="text-bold mb-3">{{ $t("Select virtual machine to restore") }}</h6>

                    <ul v-if="virtualMachines.length > 0" data-role="listview" data-view="content"
                        data-select-node="true">
                        <li v-for="vm in virtualMachines" :key="vm.Guid" data-icon="<span class='mif-server'>"
                            :data-caption="vm.Name"
                            :data-content="`<span class='text-muted'>VM Version: ${vm.Version}</span>`"
                            @click="onRadioChange(vm)" />
                    </ul>
                    <p v-else>{{ $t('No Virtual Machines available') }}</p>
                </div>
            </div>

            <div v-if="step === STEP_SELECT_DESTINATION" class="container-fluid">
                <div v-if="isLoadingDestinationStep" data-role="activity" data-type="metro" data-style="dark"></div>
                <div v-else>
                    <div class="row mt-4"><b>{{ $t('Select the destination of restore') }}:</b></div>

                    <div class="row mt-4">
                        <label>{{ $t('Virtual machine name (the name must not already exist)') }}:</label>
                    </div>
                    <div class="row">
                        <div class="cell-10">
                            <input type="text" data-role="input" v-model.trim="suggestedRestoredVmName" />
                        </div>
                    </div>
                    <div class="row mt-4">
                        <input type="radio" data-role="radio" id="default" value="Default" v-model="picked"
                            :data-caption="$t('Copy the disk file to the default folder')" name="copyFileIn" />
                    </div>
                    <div class="row mt-0">
                        <input type="radio" data-role="radio" id="custom" value="Custom" v-model="picked"
                            :data-caption="$t('Copy the disk file in the following folder')" name="copyFileIn" />
                    </div>
                    <div class="row mt-1">
                        <div class="cell-8">
                            <input type="text" v-if="customCopyFilePath.length === 0" data-role="input"
                                :class="{ disabled: picked !== 'Custom' }">
                            <input type="text" v-else data-role="input" v-model="customCopyFilePath[0].path"
                                :class="{ disabled: picked !== 'Custom' }" v-path="$ONEServiceType.FileFolder">
                        </div>
                        <div class="cell-4">
                            <button class="button primary ml-2" :class="{ disabled: picked !== 'Custom' }"
                                @click="toggleFileSystemSidebar_CustomCopyPath()">
                                {{ $t('Select path') }}
                            </button>
                        </div>
                    </div>
                    <div class="row mt-4">
                        <input type="checkbox" data-role="checkbox" id="newid" name="newid" data-style="1"
                            v-model="newId" :data-caption="$t('Generate new Id if the virtual machine already exists')">
                    </div>
                </div>
            </div>

            <div v-if="step === STEP_COMPATIBILITY" class="container-fluid">
                <div v-if="HyperVTestVmReport" class="row mt-3">
                    <b>{{ $t('Virtual machine compatibility report') }}:</b>
                </div>

                <div class="w-100 text-center" v-if="!scanTest">
                    <span class="mif-spinner2 ani-spin mif-3x"></span>
                </div>
                <div v-else>
                    <div class="row mt-3">
                        <label>{{ $t('Virtual Machine information') }}</label>
                    </div>

                    <ObjectView :obj="scanTest" :withCopyButton="false" />

                    <div class="row mt-3">
                        <label>{{ $t('Compatibility issue') }}</label>
                    </div>

                    <div v-if="incompatIssue.length > 0" id="incompatIssue" class="pb-4 w-100"
                        style="overflow-y: scroll; overflow-x: hidden;">
                        <table data-role="sorter" data-sort-dir="desc" class="table striped text-small"
                            data-show-search="true" data-show-rows-steps="false" data-search-min-length="3"
                            data-search-threshold="300">
                            <thead>
                                <tr>
                                    <td data-name="errorId" data-sortable="true" data-format="int">{{ $t('Issue id') }}
                                    </td>
                                    <td data-name="errorMessage" data-sortable="true">{{ $t('Issue description') }}</td>
                                    <td data-name="isSolvable" data-sortable="true">{{ $t('Solvability') }}</td>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="issue in incompatIssue" :key="issue.errorId">
                                    <td>{{ issue.errorId }}</td>
                                    <td>{{ issue.errorMessage }}</td>
                                    <td>{{ issue.isSolvable }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="row mt-3" v-else>
                        <span class="mif-verified fg-green mif-2x mr-2"></span>
                        <span class="label">{{ $t('No issues found') }}</span>
                    </div>
                </div>
            </div>

            <div v-if="step === STEP_SUMMARY" class="container-fluid">
                <div class="row mt-4 mb-2"><b>{{ $t('Summary') }}:</b></div>

                <ul data-role="listview" data-view="content" data-select-node="false">
                    <li class="node">
                        <span class="icon"><span class="mif-server"></span></span>
                        <div class="data">
                            <div class="caption">{{ 'localhost on ' + getPCOSFullVersion() }}</div>
                            <div class="content"><span class="text-muted">{{ $t('Server') }}</span></div>
                        </div>
                    </li>

                    <li class="node">
                        <span class="icon"><span class="mif-folder"></span></span>
                        <div class="data">
                            <div class="caption">{{ restore.source_vmFolder }}</div>
                            <div class="content"><span class="text-muted">{{ $t('Backup path') }}</span></div>
                        </div>
                    </li>

                    <li class="node">
                        <span class="icon"><span class="mif-stack"></span></span>
                        <div class="data">
                            <div class="caption">{{ summarySelectedBackup }}</div>
                            <div class="content"><span class="text-muted">{{ $t('Selected backup') }}</span></div>
                        </div>
                    </li>

                    <li class="node">
                        <span class="icon"><span class="mif-desktop_windows"></span></span>
                        <div class="data">
                            <div class="caption">{{ restore.destination_vmName }}</div>
                            <div class="content"><span class="text-muted">{{ $t('Name of the target VM') }}</span></div>
                        </div>
                    </li>

                    <li v-if="shouldRunCompatibilityTest" class="node">
                        <span class="icon"><span class="mif-codescan"></span></span>
                        <div class="data">
                            <div class="caption">{{ computedCaption }}</div>
                            <div class="content"><span class="text-muted">{{ $t('Compatibility report') }}</span></div>
                        </div>
                    </li>
                </ul>
            </div>

            <div v-if="step === STEP_RT_RESTORE && restore_rt" class="container-fluid">
                <div class="row mt-4 ml-2">
                    <div class="cell-8">
                        <label>{{ restore_rt.restorename || restore_rt.vmname }}</label>
                        <template v-if="restore_rt.type_name || restore_rt.host">
                            <br>
                            <span v-if="restore_rt.type_name" class="badge inline tertiary mt-2">{{
                                restore_rt.type_name }}</span>
                            <span v-if="restore_rt.host" class="badge inline tertiary mt-2">{{ restore_rt.host
                            }}</span>
                        </template>
                    </div>
                    <div class="cell-8">
                        <label v-if="restore_rt.dt_start_utc">{{ restore_rt.dt_start_utc.substr(0, 8) + 'T' +
                            restore_rt.dt_start_utc.substr(8) | utcAsLocal | moment('LLLL') }}</label>
                    </div>
                    <div class="cell-8">
                        <LastResult :lastresult="restore_rt.lastresult" />
                    </div>
                    <div class="cell-6">
                        <div v-if="restore_rt.progress != null">
                            <div class="clear">
                                <small v-if="restore_rt.current_operation && restore_rt.current_operation.length <= 50"
                                    class="place-left" data-role="hint" data-hint-position="top"
                                    :data-hint-text="restore_rt.current_operation">{{
                                        restore_rt.current_operation
                                    }}</small>
                                <small v-else-if="restore_rt.current_operation" class="place-left" data-role="hint"
                                    data-hint-position="top" :data-hint-text="restore_rt.current_operation">{{
                                        restore_rt.current_operation.substr(0, 50) + '...' }}</small>
                            </div>
                            <div v-if="restore_rt.progress !== 0" data-role="progress" data-type="line"
                                :data-value="restore_rt.progress" data-small="true" data-cls-bar="bg-cobalt"
                                data-role-progress="true" class="progress small">
                                <div class="bar ribbed-cobalt" :style="{ width: restore_rt.progress + '%' }"></div>
                            </div>
                        </div>
                    </div>
                    <div class="cell-2">
                        <small v-if="restore_rt.isRunning" class="place-right text-bold enlarge-1">{{
                            restore_rt.progress
                        }}%</small>
                    </div>
                </div>
            </div>
        </div>

        <div class="dialog-actions">
            <ClientMessageView :clampLines="3" ref="HVMessages" :commands="['hyperv_checkpathandgetbackupdetails']" />
            <button class="button float-right" @click="$root.$emit('CLOSERESTOREDIALOG')">{{ $t('Close') }}</button>
            <button class="button primary" v-if="step !== currentFirstStep && step !== STEP_RT_RESTORE" @click="back">{{
                $t('Back') }}
            </button>
            <div v-if="wait"><span class="mif-spinner2 ani-spin" /></div>
            <button class="button primary" v-if="!wait && step !== STEP_SUMMARY && step !== STEP_RT_RESTORE"
                :disabled="isNextButtonDisabled" v-disable-tooltip:nextButtonDisabled_message @click="next">{{
                    $t('Next')
                }}</button>
            <button class="button primary float-right" v-if="step === STEP_SUMMARY" @click="startRestore">{{
                $t('Restore')
            }}</button>
        </div>
    </div>
</template>

<script>
import DialogTitle from "../../utils/DialogTitle";
import { isValid, prettyBytes } from '../../../utils/utilitiesmodule';
import LastResult from "../../utils/LastResult";
import { draggableMixin } from "../../../mixins/draggable-mixin";
import SARepoSelect from "../../utils/SARepoSelect.vue";
import ObjectView from "../view/ObjectView.vue";
import moment from 'moment';
import SelectedResourcesLayout from "../../utils/SelectedResourcesLayout.vue";
import ClientMessageView from "../view/ClientMessageView.vue";
import { ClientMessage } from "../../../utils/objects";
import { getSARootPath } from "../../../utils/entityMethods/serviceAccount";

const STEP_SELECT_SOURCE = 0;
const STEP_SELECT_COPY = 1;
const STEP_SELECT_VM = 2;
const STEP_SELECT_DESTINATION = 3;
const STEP_COMPATIBILITY = 4;
const STEP_SUMMARY = 5;
const STEP_RT_RESTORE = 6;


export default {
    name: "HyperVRestore",
    mixins: [draggableMixin],
    props: {
        restore: {
            type: Object,
            required: true,
        },
    },
    components: {
        DialogTitle,
        LastResult,
        SARepoSelect,
        SelectedResourcesLayout,
        ObjectView,
        ClientMessageView
    },
    data() {
        return {
            STEP_SELECT_SOURCE,
            STEP_SELECT_COPY,
            STEP_SELECT_VM,
            STEP_SELECT_DESTINATION,
            STEP_COMPATIBILITY,
            STEP_SUMMARY,
            STEP_RT_RESTORE,

            step: STEP_SELECT_COPY,

            virtualMachines: [],
            wait: true,
            waitvm: true,


            justOneGuid: '',
            restore_rt: null,
            connection: null,

            job: {},
            selectedlog: {},
            logs: [],
            selectedVm: null,
            selectedManualBackup: null,
            HyperVBackupDetails: null,
            selBackupInfo: {
                date: '',
                number: '',
                dimensione: 0,
                tipo: '',
            },

            vmtorestore: '',
            suggestedRestoredVmName: '',
            customCopyFilePath: [],
            picked: 'Default',
            newId: true,
            canRestore: false,

            sourceData: {
                serviceAccount: null,
                client: null,
                selectedNodes: [],
            },

            HyperVTestVMCompatibilityModel: {
                NetServiceAccountID: "",
                VmBackupFolder: '',
                BackupType: '',
                CopyNumber: '',
                VmGuid: '',
                ckbNewVmId: '',
                LogFilePath: '',
                ReturnedError: '',
                result: 0,
                status: '',
            },
            HyperVTestVmReport: {},
            scanTest: null,
            incompatIssue: [],
            chooseSourceType: false,

            listBackups: [],

            lastFetchedSourcePath: '',
        };
    },
    computed: {
        isAutomaticMode() {
            return !!this.restore.idBackup;
        },
        currentFirstStep() {
            return this.isAutomaticMode ? this.STEP_SELECT_COPY : this.STEP_SELECT_SOURCE;
        },
        shouldRunCompatibilityTest() {
            return [this.$ONEServiceType.FileFolder, this.$ONEServiceType.Network].includes(this.restore.source_type);
        },
        isLoadingDestinationStep() {
            return this.isAutomaticMode ? !this.virtualMachines : (!this.HyperVBackupDetails || !this.HyperVBackupDetails.ibvhconfig);
        },
        computedCaption() {
            return !this.canRestore
                ? this.$t('Not solvable issue')
                : (this.incompatIssue.length > 0 ? this.$t('Solvable issue') : this.$t('Ok'));
        },
        summarySelectedBackup() {
            if (this.isAutomaticMode) {
                return [
                    this.convertDate(this.selectedlog.dt_start_utc),
                    this.restore.copyNumber,
                    this.prettyBytes(this.selectedlog.backup_size, 2),
                    this.restore.backupType,
                ].filter(Boolean).join(' ');
            }

            return [
                this.convertDate(this.selBackupInfo.date),
                this.restore.copyNumber,
                this.prettyBytes(this.selBackupInfo.dimensione, 2),
                this.restore.backupType,
            ].filter(Boolean).join(' ');
        },
        nextButtonState() {
            if (this.step === this.STEP_SELECT_SOURCE) {
                const path = this.sourceData.selectedNodes[0]?.path?.trim() || '';
                if (this.restore.source_type === -1) {
                    return { disabled: true, message: this.$t('Choose source type before continuing.') };
                }
                if (this.restore.source_type !== this.$ONEServiceType.FileFolder && this.sourceData.client == null) {
                    return { disabled: true, message: this.$t('Select and validate a service account') };
                }
                if (!path) {
                    return { disabled: true, message: this.$t('Add at least one source path.') };
                }
            }

            if (this.step === this.STEP_SELECT_COPY) {
                if (this.isAutomaticMode) {
                    if (this.restore.source_type !== this.$ONEServiceType.FileFolder && this.sourceData.client == null) {
                        return { disabled: true, message: this.$t('Select and validate a service account') };
                    }
                    if (!this.selectedlog.id) {
                        return { disabled: true, message: this.$t('Select a job before continuing.') };
                    }
                } else if (!this.selectedManualBackup) {
                    return { disabled: true, message: this.$t('Select a backup directory before continuing.') };
                }
            }

            if (this.step === this.STEP_SELECT_VM && !this.vmtorestore) {
                return { disabled: true, message: this.$t('Select a virtual machine before continuing.') };
            }

            if (this.step === this.STEP_SELECT_DESTINATION) {
                if (!this.suggestedRestoredVmName) {
                    return { disabled: true, message: this.$t('Enter a virtual machine name to continue the restore.') };
                }
                if (this.picked === 'Custom' && !this.customCopyFilePath[0]?.path) {
                    return { disabled: true, message: this.$t('Select a path for the virtual machine to continue the restore.') };
                }
            }

            if (this.step === this.STEP_COMPATIBILITY && !this.canRestore) {
                return { disabled: true, message: this.$t('Restore is not available for the current selection.') };
            }

            return { disabled: false, message: '' };
        },
        isNextButtonDisabled() {
            return this.nextButtonState.disabled;
        },
        nextButtonDisabled_message() {
            return this.nextButtonState.message;
        },
    },
    created() {
        this.step = this.currentFirstStep;
        if (this.isAutomaticMode) {
            this.getLogsList();
        } else {
            this.wait = false;

            this.chooseSourceType = this.$ONEServiceTypeInfo[this.restore.type].backupDestinationTypes.length != 1

            if (!this.chooseSourceType) {
                this.setSourceType(this.$ONEServiceType.FileFolder);
            }

        }
    },
    methods: {
        getSARootPath,

        async setSourceType(type) {
            this.restore.source_type = -1;
            await new Promise(resolve => setTimeout(resolve, 100));
            this.restore.source_type = type;
            this.restore.source_id_service_account = -2;
            this.sourceData.client = null;
            this.sourceData.serviceAccount = null;
            this.sourceData.selectedNodes = [];
            this.lastFetchedSourcePath = '';
            this.HyperVBackupDetails = null;
            this.listBackups = [];
            this.selectedManualBackup = null;
        },
        async sendSATestResult(testResult) {
            this.restore.source_id_service_account = testResult.serviceAccount.id;
            this.sourceData.serviceAccount = testResult.serviceAccount;
            this.sourceData.client = testResult.client;
        },
        async getLogsList() {
            this.wait = true;
            const response = await this.$api.getBackupLogs(this.restore.idBackup);
            this.logs = response || [];
            if (this.logs.length === 1) {
                await this.getJobConfiguration(this.logs[0]);
            }
            this.wait = false;
        },
        async getJobConfiguration(log) {
            this.selectedlog = log || {};
            this.selectedVm = null;
            this.vmtorestore = '';
            const response = await this.$api.getBackupLogConfiguration(log.id);
            this.job = Object.assign({}, response || {});

            if (this.job.destinations && this.job.destinations[0]) {
                this.restore.source_type = this.job.destinations[0].type;
                this.restore.source_id_service_account = this.job.destinations[0].id_service_account;
            }

            if (
                this.restore.source_type !== this.$ONEServiceType.FileFolder &&
                this.$session.getServiceAccountWithID(this.restore.source_id_service_account) == null
            ) {
                this.restore.source_id_service_account = "";
            }

            this.populateVMArray();
        },
        populateVMArray() {
            this.waitvm = true;
            this.virtualMachines = (this.job?.sources ?? [])
                .flatMap(source => source?.hvSource ?? [])
                .filter(vm => vm?.Selected);

            if (this.virtualMachines.length === 1) {
                this.onRadioChange(this.virtualMachines[0]);
            }

            setTimeout(() => {
                this.waitvm = false;
            }, 300);
        },
        onRadioChange(vm) {
            this.selectedVm = vm;
            this.vmtorestore = vm.Name;
            const uniqueString = this.$moment(new Date()).format('YYYYMMDDhhmmss');
            this.suggestedRestoredVmName = `${vm.Name}_restored_${uniqueString}`;
        },
        putBackupInHyperVRestoreModel(jobInfo) {
            this.resetCompatibilityState();
            this.selectedManualBackup = jobInfo;
            this.selBackupInfo.date = jobInfo.dt;
            this.selBackupInfo.number = jobInfo.numOrder;
            this.selBackupInfo.dimensione = jobInfo.dirSize;
            this.selBackupInfo.tipo = this.backupTypeToName(jobInfo.backupType, jobInfo.numOrder);
            this.restore.backupType = this.HyperVBackupDetails.ibvhconfig.backupType;
            this.restore.copyNumber = (jobInfo.numOrder === 0 || jobInfo.numOrder === '0') ? 'FULL' : String(jobInfo.numOrder).padStart(3, '0');
            this.restore.source_vmGuid = this.HyperVBackupDetails.ibvhconfig.vmId;
            this.restore.source_vmFolder = this.HyperVBackupDetails.vmFolder.path;
        },
        async getHyperVBackupConfigurationFromFileSystem(vmFolderNode) {
            if (!vmFolderNode) return false;

            this.$refs.HVMessages.setClientMessage(null);
            this.wait = true;
            try {
                this.HyperVBackupDetails = {
                    vmFolder: vmFolderNode,
                    type: this.restore.source_type,
                    id_service_account: this.restore.source_id_service_account
                };
                this.HyperVBackupDetails = await this.$root.socket("HYPERV_CHECKPATHANDGETBACKUPDETAILS", this.HyperVBackupDetails);

                if (this.HyperVBackupDetails == null) {
                    let errorMessage = ClientMessage.error("An error occurred while checking path {0}", [vmFolderNode.path]);
                    this.$refs.HVMessages.setClientMessage(errorMessage);
                    return false;
                }

                if (this.HyperVBackupDetails.error.length != 0) {
                    let errorMessage = ClientMessage.error_translated(backupConfig.error.map(e => this.$t(e)).join("\n"));
                    this.$refs.HVMessages.setClientMessage(errorMessage);
                    return false;
                }

                if (this.HyperVBackupDetails.ibvhconfig) {
                    this.listBackups.update(this.HyperVBackupDetails.ibvhconfig.listBackups);
                    console.debug(this.listBackups);
                    this.$refs.HVMessages.setClientMessage(null);
                    return true;
                }
            } catch (e) {
                console.error(e);

            } finally { this.wait = false; }
        },
        getJobInfoContent(jobInfo) {
            var str = "<span class='text-muted pt-2'>";
            if (jobInfo.numOrder.padStart(3, '0') !== '000') {
                str += this.$t('Copy number') + ' <b>' + jobInfo.numOrder.padStart(3, '0') + '</b> ';
            }
            str += this.$t('Type') + ' <b>' + this.backupTypeToName(jobInfo.backupType, jobInfo.numOrder) + '</b> ';
            str += this.$t('Size') + ' <b>' + this.prettyBytes(jobInfo.dirSize, 2) + '</b>';
            str += "</span>";
            return str;
        },
        async next() {
            try {
                this.$root.$emit("closeFileSystemSidebar");

                if (!this.isAutomaticMode && this.step === this.STEP_SELECT_SOURCE) {
                    const currentPath = this.sourceData.selectedNodes[0]?.path || '';
                    const cacheValid = currentPath
                        && currentPath === this.lastFetchedSourcePath
                        && this.HyperVBackupDetails
                        && this.HyperVBackupDetails.ibvhconfig;

                    if (cacheValid) {
                        this.step = this.STEP_SELECT_COPY;
                        return;
                    }

                    var ok = await this.getHyperVBackupConfigurationFromFileSystem(this.sourceData.selectedNodes[0]);
                    if (ok) {
                        this.lastFetchedSourcePath = currentPath;
                        this.step = this.STEP_SELECT_COPY;
                    }
                    return;
                }

                if (this.step === this.STEP_SELECT_COPY) {
                    if (this.isAutomaticMode) {
                        this.step = this.STEP_SELECT_VM;
                    } else {
                        this.suggestedRestoredVmName = `${this.HyperVBackupDetails.ibvhconfig.vmName}_restored_${moment().format('YYYYMMDDhhmmss')} `;
                        this.step = this.STEP_SELECT_DESTINATION;
                    }
                    return;
                }

                if (this.step === this.STEP_SELECT_VM) {
                    this.step = this.STEP_SELECT_DESTINATION;
                    return;
                }

                if (this.step === this.STEP_SELECT_DESTINATION) {
                    this.prepareRestoreModel();

                    if (!this.shouldRunCompatibilityTest) {
                        this.step = this.STEP_SUMMARY;
                        return;
                    }

                    if (!this.HyperVTestVmReport.status) {
                        this.prepareCompatibilityModel();
                        this.step = this.STEP_COMPATIBILITY;
                        await this.RunCompatibilityTest(this.HyperVTestVMCompatibilityModel);
                        return;
                    }
                }

                if (this.step === this.STEP_COMPATIBILITY) {
                    this.step = this.STEP_SUMMARY;
                }

            } catch (e) { console.error(e); }
        },
        back() {
            this.$root.$emit("closeFileSystemSidebar");

            if (this.step === this.STEP_SUMMARY) {
                this.step = this.shouldRunCompatibilityTest ? this.STEP_COMPATIBILITY : this.STEP_SELECT_DESTINATION;
                return;
            }

            if (this.step === this.STEP_COMPATIBILITY) {
                this.step = this.STEP_SELECT_DESTINATION;
                return;
            }

            if (this.step === this.STEP_SELECT_DESTINATION) {
                this.step = this.isAutomaticMode ? this.STEP_SELECT_VM : this.STEP_SELECT_COPY;
                return;
            }

            if (this.step === this.STEP_SELECT_VM) {
                this.step = this.STEP_SELECT_COPY;
                return;
            }

            if (this.step === this.STEP_SELECT_COPY && !this.isAutomaticMode) {
                this.step = this.STEP_SELECT_SOURCE;
                return;
            }
        },
        prepareRestoreModel() {
            this.justOneGuid = this.uuidv4();
            this.restore.Id = this.justOneGuid;
            this.restore.generateNewId = this.newId;
            this.restore.destination_vmName = this.suggestedRestoredVmName;
            this.restore.destination_vhdCustomFolder = this.customCopyFilePath[0]?.path || '';

            if (this.isAutomaticMode) {
                this.restore.jobName = this.job.name || this.restore.jobName;
                this.restore.source_type = this.job.destinations?.[0]?.type ?? this.restore.source_type;
                this.restore.source_id_service_account = this.job.destinations?.[0]?.id_service_account ?? this.restore.source_id_service_account;
                this.restore.source_vmFolder = `${this.job.destinations?.[0]?.path || ''}${this.$ONEServiceTypeInfo[this.restore.source_type]?.slash || '\\'}${this.vmtorestore} `;
                this.restore.source_vmGuid = this.selectedVm?.Guid || '';
                this.restore.backupType = `${this.job.options?.rct == 1 ? 'RCT-' : ''}${this.job.options?.backupType || ''} `;
                this.restore.copyNumber = this.selectedlog.backup_number == 0 ? 'FULL' : String(this.selectedlog.backup_number).padStart(3, '0');
            } else {
                this.restore.source_vmFolder = this.HyperVBackupDetails.vmFolder.path;
                this.restore.source_vmGuid = this.HyperVBackupDetails.ibvhconfig?.vmId || '';
                this.restore.backupType = this.HyperVBackupDetails.ibvhconfig?.backupType || '';
                this.restore.copyNumber = this.selBackupInfo.number == 0 ? 'FULL' : String(this.selBackupInfo.number).padStart(3, '0');
                this.restore.jobName = this.HyperVBackupDetails.ibvhconfig?.jobName || this.restore.jobName;
            }
        },
        prepareCompatibilityModel() {
            this.HyperVTestVMCompatibilityModel.NetServiceAccountID = this.restore.source_id_service_account;
            this.HyperVTestVMCompatibilityModel.VmBackupFolder = this.restore.source_vmFolder;
            this.HyperVTestVMCompatibilityModel.BackupType = this.restore.backupType;
            this.HyperVTestVMCompatibilityModel.CopyNumber = this.restore.copyNumber;
            this.HyperVTestVMCompatibilityModel.VmGuid = this.restore.source_vmGuid;
            this.HyperVTestVMCompatibilityModel.ckbNewVmId = this.newId;
            this.HyperVTestVMCompatibilityModel.LogFilePath = '';
            this.HyperVTestVMCompatibilityModel.ReturnedError = '';
            this.HyperVTestVMCompatibilityModel.result = 0;
            this.HyperVTestVMCompatibilityModel.status = '';
        },
        async RunCompatibilityTest(HVTestModel) {
            const testres = await this.$root.socket("HYPERV_COMPATIBILITYTEST", HVTestModel);
            if (!testres) return;

            this.HyperVTestVmReport = testres;
            this.scanTest = testres.scan && testres.scan.length > 0 ? testres.scan[0] : null;
            this.incompatIssue = testres.incompatibilityModels || [];
            this.canRestore = !this.incompatIssue.some(issue => !issue.isSolvable);
        },
        async startRestore() {
            this.$root.$emit("closeFileSystemSidebar");

            this.$root.$on("startRestore_feedback", ok => {
                if (ok.status === this.$ResultValue.OK) {
                    this.startRestore_confirmed();
                }
                this.$root.$off("startRestore_feedback");
            });

            this.$root.$emit("startRestore", this.restore.jobName || this.suggestedRestoredVmName);
        },
        startRestore_confirmed() {
            this.step = this.STEP_RT_RESTORE;
            this.$root.socket("HYPERV_RUNRESTORE", this.restore);
            this.$root.$on("addRestoreToPage", restores => {
                if (restores) {
                    this.restore_rt = restores[this.justOneGuid];
                }
            });
        },
        toggleFileSystemSidebar() {
            var rootPath = this.getSARootPath(this.sourceData.serviceAccount);

            const config = {
                type: this.restore.source_type,
                includeFiles: false,
                multipleChoice: false,
                client: this.sourceData.client,
                isRootSelectable: false,
                rootPath: rootPath
            };
            this.$root.$emit("toggleFileSystemSidebar", this.sourceData.selectedNodes, config);
        },
        toggleFileSystemSidebar_CustomCopyPath() {

            const config = {
                type: this.$ONEServiceType.FileFolder,
                includeFiles: false,
                multipleChoice: false,
                client: null,
                isRootSelectable: false,
                rootPath: rootPath

            };
            this.$root.$emit("toggleFileSystemSidebar", this.customCopyFilePath, config);
        },
        resetCompatibilityState() {
            this.HyperVTestVmReport = {};
            this.scanTest = null;
            this.incompatIssue = [];
            this.canRestore = false;
        },
        backupTypeToName(tipo, numOrder) {
            if (numOrder == '0') return 'Full';

            switch ((tipo || '').toLowerCase().trim()) {
                case 'f': return this.$t('Complete (Full)');
                case 'i': return this.$t('Incremental');
                case 'd': return this.$t('Differential');
                default: return '';
            }
        },
        convertDate(date) {
            try {
                const parsed = this.$moment((date || '').substr(0, 8) + 'T' + (date || '').substr(8), 'YYYYMMDD HH:mm:ss');
                return this.$moment.utc(parsed).format('ddd DD/MM/YYYY HH:mm:ss');
            } catch {
                return '';
            }
        },
        prettyBytes(bytes, precision) {
            return prettyBytes(bytes, precision);
        },
        getPCOSFullVersion() {
            const pc = this.$session.getRoomPC();
            return pc ? pc.os_version_full : '';
        },
        uuidv4() {
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
                const r = Math.random() * 16 | 0;
                const v = c === 'x' ? r : ((r & 0x3) | 0x8);
                return v.toString(16);
            });
        },
        isValid(v) {
            return isValid(v);
        },
    },
    beforeDestroy() {
        this.$root.$off("socketEventConnected");
        this.$root.$off("socketEventDisconnected");
        this.$root.$off("addRestoreToPage");
        this.$root.$off("startRestore_feedback");
        if (this.connection != null) this.connection.stop();
    },
};
</script>
